/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Divider, InputNumber, message } from 'antd'
import { HistoryOutlined, LockOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type { ImportSeason } from '@sportninja/common/services/teamBalance'
import BaseModal from 'src/components/ts-components/BaseModal'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import {
  captionXSmall,
  paragraphMedium,
  paragraphSmall,
} from 'src/components/css'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import EmphasisText from './EmphasisText'
import { SkeletonBar } from './ImportPreviousSeasonsSkeleton'

const MIN_SLOTS = 1
const MAX_SLOTS = 60

type FirstRunModalProps = {
  open: boolean
  submitting: boolean
  onSubmit: (count: number) => Promise<unknown>
  onClose: () => void
  /**
   * Jeff, Oct 1 — the previous seasons the rep may import players from, newest
   * first. None (once loaded) is exactly the dialog as it was before the import.
   */
  seasons?: readonly ImportSeason[]
  /** Their first load is on its way: the door holds a placeholder meanwhile. */
  seasonsLoading?: boolean
  /** Their load failed: the door says so, and `onRetrySeasons` asks again. */
  seasonsError?: boolean
  onRetrySeasons?: () => void
  /** Importing sends invites: an approved registration, its budget unlocked. */
  canImport?: boolean
  /** Why the import is off (approval, the lock), said under its button. */
  importBlockedReason?: string | null
  /** Close this and open Import Players From Previous Seasons. */
  onChooseImport?: () => void
}

/**
 * Spec flow (b), "Expected Player Slots": how many players will split the team
 * fee — creates N open slots; the fee auto-divides penny-exact on creation.
 *
 * James, Jul 31: the modal is named for what it ASKS ("Expected Player Slots"),
 * not for the artefact it sets up, and the body no longer names the division —
 * the admin opened this from that division's sheet, so restating it was noise
 * that also forced an interpolation through a translation.
 *
 * Jeff, Oct 2 (F-A, the import first) — a team that played before is offered
 * its players first: "{team} played {season} with N players." and a button
 * that opens Import Players From Previous Seasons, then "or" and the count, as
 * before. Without a previous season the dialog is the count alone. A door the
 * rep can't use yet stays, disabled, and says why (approval, the lock).
 */
const FirstRunModal = ({
  open,
  submitting,
  onSubmit,
  onClose,
  seasons = [],
  seasonsLoading = false,
  seasonsError = false,
  onRetrySeasons,
  canImport = false,
  importBlockedReason = null,
  onChooseImport,
}: FirstRunModalProps) => {
  const [count, setCount] = useState<number>(10)

  const latest = seasons[0] ?? null
  // Still loading or failed: the door may yet have something to offer, so its
  // place is kept rather than the dialog changing shape twice.
  const hasDoor = seasonsLoading || seasonsError || latest !== null

  const clamp = (value: number): number =>
    Math.min(Math.max(Math.round(value || MIN_SLOTS), MIN_SLOTS), MAX_SLOTS)

  const submit = () => {
    onSubmit(clamp(count)).catch((error) => {
      message.error((error as Error)?.message || 'Unable to create the slots.')
    })
  }

  const door = seasonsLoading ? (
    <div
      css={doorStyle}
      aria-hidden='true'
      data-testid='first-run-door-skeleton'
    >
      <SkeletonBar width='72%' height={16} />
      <SkeletonBar width='100%' height={40} radius={8} />
    </div>
  ) : seasonsError ? (
    <div css={doorErrorStyle} data-testid='first-run-door-error'>
      <p css={leadStyle}>{balanceText('common:balanceImportSeasonsError')}</p>
      <HeaderActionButton
        variant='secondary'
        size='small'
        textWeight='regular'
        text={balanceLabel('common:balanceRetry')}
        onClick={onRetrySeasons}
        data-testid='first-run-door-retry'
      />
    </div>
  ) : latest ? (
    <div css={doorStyle}>
      <p css={leadStyle} data-testid='first-run-import-lead'>
        <EmphasisText
          text={balanceText('common:balanceFirstRunImportLead', {
            team: latest.team.name,
            season: latest.schedule_name ?? '',
            count: latest.player_count,
          })}
          emphasis={[latest.team.name, latest.schedule_name]}
        />
      </p>
      <HeaderActionButton
        variant='outlined'
        fullWidth
        icon={<HistoryOutlined css={historyIconStyle} />}
        text={balanceLabel('common:balanceImportOpen')}
        onClick={onChooseImport}
        disabled={!canImport}
        data-testid='first-run-import'
      />
      {importBlockedReason ? (
        <p css={lockLineStyle} data-testid='first-run-import-blocked'>
          <LockOutlined css={lockIconStyle} />
          <span>{importBlockedReason}</span>
        </p>
      ) : null}
    </div>
  ) : null

  return (
    <BaseModal
      title={balanceLabel('common:balanceFirstRunTitle')}
      isOpen={open}
      onClose={onClose}
      // The door's sentence and button need the extra width; the count alone
      // keeps the size it always had.
      desktopWidth={hasDoor ? '540px' : '480px'}
      destroyOnClose
      /**
       * The house action button is `HeaderActionButton`, NOT an antd Button —
       * that is what "Add Player Slot" on this tab and Registration Setup are.
       * antd's default footer renders its own Buttons, which left this modal's
       * confirm as the only primary action on the tab that did not match.
       *
       * `size='small'` + `textWeight='regular'` deliberately: that is exactly what
       * Add Player Slot beside it uses (32px, 12px label). The 40px `default`
       * size renders a 16px label, which made the footer read a full step heavier
       * than every other action on the sheet.
       */
      actions={
        <div css={footerStyle}>
          <HeaderActionButton
            variant='secondary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:cancel')}
            onClick={onClose}
            disabled={submitting}
            data-testid='first-run-cancel'
          />
          {/* SN-8467 (Jeff, Oct 1) — says what it makes, like the toolbar's dialog. */}
          <HeaderActionButton
            variant='primary'
            size='small'
            textWeight='regular'
            text={balanceLabel('common:balanceAddSlotSubmit')}
            onClick={submit}
            isLoading={submitting}
            data-testid='first-run-submit'
          />
        </div>
      }
    >
      <div css={bodyStyle}>
        {hasDoor ? (
          <>
            {door}
            <Divider plain css={orStyle} data-testid='first-run-or'>
              {balanceText('common:balanceFirstRunOr')}
            </Divider>
          </>
        ) : null}
        <div css={countFormStyle}>
          <p css={leadStyle} data-testid='first-run-body'>
            {balanceText('common:balanceFirstRunBody')}
          </p>
          <div css={countRowStyle}>
            <InputNumber
              min={MIN_SLOTS}
              max={MAX_SLOTS}
              value={count}
              onChange={(value) => setCount(clamp(Number(value ?? MIN_SLOTS)))}
              aria-label={balanceLabel('common:balanceFirstRunTitle')}
              data-testid='first-run-count'
              css={numberInputStyle}
            />
            <span css={hintStyle}>
              {balanceText('common:balanceFirstRunCountHint')}
            </span>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}

export default FirstRunModal

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`
const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
/** Both halves' sentences: body size, muted, the names in white (EmphasisText). */
const leadStyle = css`
  ${paragraphMedium};
  margin: 0;
  color: ${colors.NEUTRAL_50};
  line-height: 1.5;
`
const doorStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const doorErrorStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`
/** The house icon-in-a-button size. */
const historyIconStyle = css`
  font-size: 16px;
`
const lockLineStyle = css`
  ${paragraphSmall};
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: ${colors.NEUTRAL_70};
  line-height: 1.45;
`
const lockIconStyle = css`
  flex-shrink: 0;
`
/** "OR" between the two ways in: the house caption, a hairline either side. */
const orStyle = css`
  &.ant-divider {
    ${captionXSmall};
    margin: 0;
    color: ${colors.NEUTRAL_70};
  }
  &.ant-divider::before,
  &.ant-divider::after {
    border-block-start-color: ${colors.SECONDARY_100} !important;
  }
`
const countFormStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
/** The count and its range share a line: the range is a footnote to the field. */
const countRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`
/** House field treatment — the count field Add Player Slots uses. */
const numberInputStyle = css`
  width: 120px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};

  .ant-input-number-input {
    ${paragraphMedium};
    height: 38px;
  }
`
const hintStyle = css`
  ${paragraphSmall};
  color: ${colors.NEUTRAL_70};
`
